import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChildren
} from '@angular/core';
import { CONFIG_MODAL_INPUTS_TOKEN, ModalOverlayRef } from '@core/classes/modal-overlay.ref';
import { CurrencyExchange } from '@core/models/currency-exchange';
import { DummyInsuranceTarget, InsuranceTarget } from '@core/models/insurance-target.model';
import { InvestmentData } from '@core/models/investment.model';
import { LifePlan } from '@core/models/life-plan.model';
import {
  calculateCoverageLimits,
  ProductCoverage,
  calculateProductAvailability
} from '@core/models/product-coverage.model';
import {
  adjustValuesToCurrency,
  deepCloneProduct,
  Product,
  updateProductWithLiteValuesAndLimits
} from '@core/models/product.model';
import {
  deepCloneProductsPerInsuranceTarget,
  ProductsPerInsuranceTarget,
  updateProductMilestone
} from '@core/models/products-per-insurance-target.model';
import { ConfigService } from '@core/services/config.service';
import { LiteService } from '@core/services/lite.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { Subscription } from 'rxjs';
import SwiperCore, { Navigation, Pagination, Scrollbar } from 'swiper/core';
import { ProductFeatureCoverageComponent } from './components/product-feature-coverage/product-feature-coverage.component';

SwiperCore.use([Navigation, Pagination, Scrollbar]);

@Component({
  selector: 'mfp-product-select',
  templateUrl: './product-select.component.html',
  styleUrls: ['./product-select.component.scss']
})
export class ProductSelectComponent implements OnInit, OnDestroy, AfterViewInit {
  swiperConfig = {
    allowTouchMove: true,
    breakpoints: {
      0: {
        allowTouchMove: true,
        slidesPerView: 1
      },
      768: {
        allowTouchMove: false,
        slidesPerView: 'auto' as 'auto'
      }
    },
    index: 0,
    scrollbar: {
      draggable: true
    },
    spaceBetween: 30,
    navigation: true,
    pagination: true,
    speed: 300
  };
  lifePlan!: LifePlan;
  productsPerInsuranceTarget!: ProductsPerInsuranceTarget;
  products: Product[] = [];
  currentProduct?: Product;
  defaultCurrency = '';
  currencyExchange!: CurrencyExchange;
  $storeSubscription!: Subscription;

  @ViewChildren(ProductFeatureCoverageComponent) coveragesViewChildren!: QueryList<ProductFeatureCoverageComponent>;
  coveragesComponents: ProductFeatureCoverageComponent[] = [];

  @Input() currentTarget: InsuranceTarget = DummyInsuranceTarget;
  @Input() onSave: (product: Product) => void = () => {};

  constructor(
    public dialogRef: ModalOverlayRef,
    private store: Store<AppState>,
    private uiService: UiService,
    private liteService: LiteService,
    private configService: ConfigService,
    @Optional() @Inject(CONFIG_MODAL_INPUTS_TOKEN) config: any
  ) {
    if (config) {
      this.currentTarget = config.currentTarget;
      this.onSave = config.onSave;
    }
    if (this.currentTarget.currentProduct) {
      this.currentProduct = deepCloneProduct(this.currentTarget.currentProduct);
    }
  }

  ngOnInit(): void {
    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.lifePlan = state.lifePlan.currentLifePlan;
        this.defaultCurrency = state.config.defaultCurrency;
        this.currencyExchange = state.config.currencyExchange;
        this.updateProductsPerInsuranceTarget(state.recommendedProducts.productsPerInsuranceTarget);
      }
    });
  }

  ngAfterViewInit(): void {
    this.coveragesViewChildren?.changes.subscribe({
      next: components => (this.coveragesComponents = components.toArray())
    });
  }

  ngOnDestroy(): void {
    this.$storeSubscription.unsubscribe();
  }

  updateProductsPerInsuranceTarget(productsPerInsuranceTarget: ProductsPerInsuranceTarget) {
    this.productsPerInsuranceTarget = deepCloneProductsPerInsuranceTarget(productsPerInsuranceTarget);
    this.products = [];

    for (const product of this.productsPerInsuranceTarget[this.currentTarget.id].available) {
      if (product.id !== this.currentProduct?.id) {
        this.uiService.presentLoading();
        updateProductMilestone(product, this.currentTarget);
        adjustValuesToCurrency(product, product.selectedCurrency, this.currencyExchange);
        this.liteService
          .calculateProductWithLimits(
            product,
            this.currentTarget,
            product.type === 'normal' ? product.currentAssuredAmount.amount : product.amountAtEndOfTemporality.amount,
            product.type === 'normal' ? product.currentFee.amount : product.currentInvestment.amount
          )
          .subscribe({
            next: liteResponse => {
              updateProductWithLiteValuesAndLimits(product, liteResponse);
              product.coverages.forEach(coverage => {
                calculateCoverageLimits(product, coverage, this.currentTarget, this.currencyExchange);
                calculateProductAvailability(product, coverage, this.lifePlan);
              });
              this.uiService.dismissLoading();
            },
            error: error => {
              this.uiService.dismissLoading();
              this.uiService.openErrorModal();
            }
          });
        this.products.push(product);
      } else {
        this.currentProduct.coverages.forEach(coverage => {
          calculateCoverageLimits(this.currentProduct!, coverage, this.currentTarget, this.currencyExchange);
        });
        this.products.push(this.currentProduct);
      }
    }
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (this.currentProduct && this.validProduct()) {
      this.store.dispatch(
        actions.updateLifePlanInsuranceTargetCurrentProduct({
          insuranceTargetId: this.currentTarget.id,
          product: deepCloneProduct(this.currentProduct)
        })
      );
      if (this.onSave) {
        this.onSave(this.currentProduct);
      }
      this.close();
    }
  }

  onProductChange(product: Product) {
    this.currentProduct = product;
    updateProductMilestone(this.currentProduct, this.currentTarget);
    for (const c of this.currentProduct.coverages) {
      calculateCoverageLimits(this.currentProduct, c, this.currentTarget, this.currencyExchange);
      calculateProductAvailability(this.currentProduct, c, this.lifePlan);
    }
  }

  onCoverageChange(coverage: ProductCoverage) {
    if (this.currentProduct) {
      this.currentProduct.coverages[this.currentProduct.coverages.findIndex(c => c.id === coverage.id)] = coverage;
      for (const c of this.currentProduct.coverages) {
        calculateCoverageLimits(this.currentProduct, c, this.currentTarget, this.currencyExchange);
      }
    }
  }

  onInvestmentChange(investment: InvestmentData) {
    if (this.currentProduct) {
      this.currentProduct.projectedInvestment = investment;
    }
  }

  validProduct(): boolean {
    if (this.currentProduct) {
      if (!this.currentProduct.selectedTemporality) {
        return false;
      }
      for (const coverage of this.currentProduct.coverages) {
        if (coverage.type === 'activable' && coverage.isAmountModifiable && coverage.active && !coverage.valid) {
          return false;
        }
      }

      if (this.currentProduct.projectedInvestment) {
        if (this.currentProduct.projectedInvestment.active && !this.currentProduct.projectedInvestment.valid) {
          return false;
        }
      }

      return true;
    }
    return false;
  }
}
